import type { DicomPullRequest, MetaData } from '@/src/components/backend/payload-typing';
import type { BatchResult, SegmentationSettings } from '@/src/components/segmentation/segmentation-typing';
import type { AnalysisResult } from '@/src/store/analysis/data-typing.ts';

import { BackendClassificationResultSchema, seriesClassification } from '@src/store/dicom-web/SeriesClassification';
import JSZip from 'jszip';
import { z } from 'zod';

import { BatchResultSchema } from '@/src/components/segmentation/segmentation-typing';

import { session } from '@/src/supabase';

// ── Zod Schemas & Types for Gateway API ───────────────────────────────────────

export const GatewayJobStatusSchema = z.object({
  status: z.union([
    z.enum(['queued', 'processing', 'complete', 'failed']),
    z.string(),
  ]),
  message: z.string().catch(''),
  progress: z.number().catch(0),
});
export type GatewayJobStatus = z.infer<typeof GatewayJobStatusSchema>;

export const GatewaySegmentationJobRequestSchema = z.object({
  user_id: z.string(),
  study_instance_uid: z.string().optional(),
  payload: z.array(z.custom<DicomPullRequest>()),
  metaData: z.custom<MetaData>().nullable().optional(),
  settings: z.custom<SegmentationSettings>().nullable().optional(),
});
export type GatewaySegmentationJobRequest = z.infer<typeof GatewaySegmentationJobRequestSchema>;

export const ListJobsOptionsSchema = z.object({
  userId: z.string().optional(),
  status: z.string().optional(),
  studyInstanceUid: z.string().optional(),
});
export type ListJobsOptions = z.infer<typeof ListJobsOptionsSchema>;

export const ZipResultSchema = z.object({
  batchResults: z.array(BatchResultSchema).optional(),
  analysisResponseData: z.custom<AnalysisResult>().optional(),
});
export type ZipResult = z.infer<typeof ZipResultSchema>;

export const GatewayCreateJobResponseSchema = z.object({
  job_id: z.string(),
  status: GatewayJobStatusSchema,
});
export type GatewayCreateJobResponse = z.infer<typeof GatewayCreateJobResponseSchema>;

export const GatewayImageResultSchema = z.object({
  image_uid: z.string(),
  output_url: z.string().nullable().optional(),
});
export type GatewayImageResult = z.infer<typeof GatewayImageResultSchema>;

export const GatewayJobDetailResponseSchema = z.object({
  job_id: z.string(),
  type: z.string().catch('segmentation'),
  model: z.string().nullable().optional(),
  status: GatewayJobStatusSchema,
  results: z.array(GatewayImageResultSchema).catch([]),
  zip_url: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  created_at: z.string().catch(() => new Date().toISOString()),
});
export type GatewayJobDetailResponse = z.infer<typeof GatewayJobDetailResponseSchema>;

export const GatewayJobSummarySchema = z.object({
  job_id: z.string(),
  type: z.string().catch('segmentation'),
  model: z.string().nullable().optional(),
  status: GatewayJobStatusSchema,
  error: z.string().nullable().optional(),
  created_at: z.string().catch(() => new Date().toISOString()),
});
export type GatewayJobSummary = z.infer<typeof GatewayJobSummarySchema>;

export const GatewayJobListResponseSchema = z.object({
  jobs: z.array(GatewayJobSummarySchema).catch([]),
});
export type GatewayJobListResponse = z.infer<typeof GatewayJobListResponseSchema>;

export const GatewayAbortJobResponseSchema = z.object({
  job_id: z.string(),
  status: z.string(),
});
export type GatewayAbortJobResponse = z.infer<typeof GatewayAbortJobResponseSchema>;

export const OrientationMetaWrapperSchema = z.object({
  'orientation-meta.json': BackendClassificationResultSchema,
});

export const ZipContourPayloadSchema = z.object({
  imageUID: z.string().optional(),
  contours: z.array(z.unknown()),
  // response_type: z.string().optional(),
});
export type ZipContourPayload = z.infer<typeof ZipContourPayloadSchema>;

export interface PollJobOptions {
  onProgress?: (status: GatewayJobStatus) => void;
  pollIntervalMs?: number;
  maxAttempts?: number;
  signal?: AbortSignal;
}

export interface ProcessBatchGatewayOptions {
  onProgress?: (progress: number, message: string) => void;
  signal?: AbortSignal;
  userId?: string;
  studyInstanceUid?: string;
}

// Helper to determine base URL with safe fallbacks
function getGatewayBaseUrl(): string {
  const envUrl = import.meta.env.VITE_OCG_BACKEND_GATEWAY_API;
  return envUrl.replace(/\/+$/, '');
}

/**
 * Helper function to safely parse raw JSON text without throwing unhandled exceptions.
 */
function safeJsonParse(jsonText: string, filename?: string): unknown {
  try {
    return JSON.parse(jsonText);
  }
  catch (err) {
    console.warn(`[SegmentationGateway] Failed to parse JSON text from ZIP entry (${filename || 'unknown'}):`, err);
    return null;
  }
}

/**
 * Helper function to detect and process orientation classification metadata if present using Zod validation.
 */
function handleOrientationMetaIfPresent(data: unknown, identifier?: string): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }

  const isMetaName = identifier ? identifier.toLowerCase().includes('orientation-meta') : false;

  // Case 1: Check if wrapped inside "orientation-meta.json" key
  const wrapperParse = OrientationMetaWrapperSchema.safeParse(data);
  if (wrapperParse.success) {
    console.log(`[SegmentationGateway] Found valid orientation-meta.json in (${identifier || 'content'}), updating series classification...`);
    seriesClassification.adjustClassification(wrapperParse.data['orientation-meta.json']);
    seriesClassification.applyDefaultViewportMapping();
    return true;
  }

  // Case 2: Direct BackendClassificationResult structure
  const directParse = BackendClassificationResultSchema.safeParse(data);
  if (directParse.success && Object.keys(directParse.data).length > 0) {
    console.log(`[SegmentationGateway] Found valid orientation classification data in (${identifier || 'content'}), updating series classification...`);
    seriesClassification.adjustClassification(directParse.data);
    seriesClassification.applyDefaultViewportMapping();
    return true;
  }

  // Fallback for legacy / unstrict orientation-meta file names
  if (isMetaName) {
    console.log(`[SegmentationGateway] Processing orientation file (${identifier}), fallback check...`);
    const record = data as Record<string, unknown>;
    const classification = record['orientation-meta.json'] || record;
    const fallbackParse = BackendClassificationResultSchema.safeParse(classification);
    if (fallbackParse.success) {
      seriesClassification.adjustClassification(fallbackParse.data);
      seriesClassification.applyDefaultViewportMapping();
      return true;
    }
  }

  return false;
}

/**
 * Composable providing clean API functions to interact with Gateway Service (`main.py`).
 */
export function useSegmentationGateway() {
  /**
   * POST /jobs/segmentation — Create a new segmentation job.
   */
  async function createSegmentationJob(
    req: GatewaySegmentationJobRequest,
    signal?: AbortSignal,
  ): Promise<GatewayCreateJobResponse> {
    const validReq = GatewaySegmentationJobRequestSchema.parse(req);
    const baseUrl = getGatewayBaseUrl();
    const response = await fetch(`${baseUrl}/jobs/segmentation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(validReq),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `Failed to create segmentation job (${response.status}): ${errorText || response.statusText}`,
      );
    }

    const rawData: unknown = await response.json();
    const parsed = GatewayCreateJobResponseSchema.safeParse(rawData);
    if (!parsed.success) {
      console.warn('[SegmentationGateway] createSegmentationJob validation error:', parsed.error);
      throw new Error(`Invalid Gateway response format when creating job: ${parsed.error.message}`);
    }

    return parsed.data;
  }

  /**
   * GET /jobs/{job_id} — Retrieve job detail, status, and per-image presigned URLs.
   */
  async function getJob(
    jobId: string,
    signal?: AbortSignal,
  ): Promise<GatewayJobDetailResponse> {
    const baseUrl = getGatewayBaseUrl();
    const response = await fetch(`${baseUrl}/jobs/${jobId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `Failed to fetch job ${jobId} (${response.status}): ${errorText || response.statusText}`,
      );
    }

    const rawData: unknown = await response.json();
    const parsed = GatewayJobDetailResponseSchema.safeParse(rawData);
    if (!parsed.success) {
      console.warn(`[SegmentationGateway] getJob(${jobId}) validation error:`, parsed.error);
      throw new Error(`Invalid Gateway job detail response for ${jobId}: ${parsed.error.message}`);
    }

    return parsed.data;
  }

  /**
   * GET /jobs?user_id=...&status=... — List jobs for a user.
   */
  async function listJobs(
    options: ListJobsOptions,
    signal?: AbortSignal,
  ): Promise<GatewayJobListResponse> {
    const validOptions = ListJobsOptionsSchema.parse(options);
    const { userId, status, studyInstanceUid } = validOptions;
    const baseUrl = getGatewayBaseUrl();
    const params = new URLSearchParams();

    if (userId) params.append('user_id', userId);
    if (status) params.append('status', status);
    if (studyInstanceUid) params.append('study_id', studyInstanceUid);

    const response = await fetch(`${baseUrl}/jobs?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `Failed to list jobs (${response.status}): ${errorText || response.statusText}`,
      );
    }

    const rawData: unknown = await response.json();
    const parsed = GatewayJobListResponseSchema.safeParse(rawData);
    if (!parsed.success) {
      console.warn('[SegmentationGateway] listJobs validation error:', parsed.error);
      throw new Error(`Invalid Gateway job list response format: ${parsed.error.message}`);
    }

    return parsed.data;
  }

  /**
   * POST /jobs/abort/{job_id} — Abort a queued or processing job.
   */
  async function abortJob(
    jobId: string,
    signal?: AbortSignal,
  ): Promise<GatewayAbortJobResponse> {
    const baseUrl = getGatewayBaseUrl();
    const response = await fetch(`${baseUrl}/jobs/abort/${jobId}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(
        `Failed to abort job ${jobId} (${response.status}): ${errorText || response.statusText}`,
      );
    }

    const rawData: unknown = await response.json();
    const parsed = GatewayAbortJobResponseSchema.safeParse(rawData);
    if (!parsed.success) {
      console.warn(`[SegmentationGateway] abortJob(${jobId}) validation error:`, parsed.error);
      throw new Error(`Invalid Gateway abort job response format: ${parsed.error.message}`);
    }

    return parsed.data;
  }

  /**
   * Helper: Poll GET /jobs/{job_id} until status is 'complete' or 'failed'.
   */
  async function pollJobUntilComplete(
    jobId: string,
    options: PollJobOptions = {},
  ): Promise<GatewayJobDetailResponse> {
    const {
      onProgress,
      pollIntervalMs = 1000,
      maxAttempts = 600,
      signal,
    } = options;

    let attempts = 0;
    while (attempts < maxAttempts) {
      if (signal?.aborted) {
        throw new Error('Segmentation polling aborted');
      }

      attempts++;
      const detail = await getJob(jobId, signal);

      if (onProgress) {
        onProgress(detail.status);
      }

      if (detail.status.status === 'complete') {
        return detail;
      }

      if (detail.status.status === 'failed') {
        throw new Error(
          detail.error || detail.status.message || 'Segmentation job failed on Gateway',
        );
      }

      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    throw new Error(`Timed out waiting for segmentation job ${jobId} to complete.`);
  }

  /**
   * Download and extract presigned ZIP results for a completed job.
   * Handles both standard segmentation job ZIPs and analysis job ZIPs containing
   * subdirectories `analysis_result/` and `input_masks/`.
   */
  async function fetchJobZipResults(
    zipUrl: string,
    signal?: AbortSignal,
  ): Promise<ZipResult> {
    const t0 = performance.now();

    const resp = await fetch(zipUrl, { signal });
    if (!resp.ok) {
      throw new Error(`Failed to download job ZIP archive (${resp.status}): ${resp.statusText}`);
    }

    const arrayBuffer = await resp.arrayBuffer();
    const downloadElapsedMs = performance.now() - t0;

    const tUnzip0 = performance.now();
    const zip = await JSZip.loadAsync(arrayBuffer);
    const unzipElapsedMs = performance.now() - tUnzip0;

    const fileKeys = Object.keys(zip.files).filter(filename => !zip.files[filename].dir && filename.endsWith('.json'));

    const hasAnalysisResultFolder = fileKeys.some(fn => fn.startsWith('analysis_result/'));

    const parseT0 = performance.now();

    if (hasAnalysisResultFolder) {
      let analysisResponseData: AnalysisResult | undefined;

      // 1. Extract analysisResponseData from analysis_result/
      const analysisResultFile = fileKeys.find(fn => fn.startsWith('analysis_result/'));
      if (analysisResultFile) {
        try {
          const jsonText = await zip.files[analysisResultFile].async('string');
          const rawData = safeJsonParse(jsonText, analysisResultFile);
          if (rawData && typeof rawData === 'object') {
            analysisResponseData = rawData as AnalysisResult;
          }
        }
        catch (err) {
          console.warn(`[SegmentationGateway] Failed reading analysis file ${analysisResultFile}:`, err);
        }
      }

      const parseElapsedMs = performance.now() - parseT0;
      const totalElapsedMs = performance.now() - t0;
      console.log(
        `Completed analysis ZIP retrieval in ${totalElapsedMs.toFixed(2)} ms total `
        + `(Download: ${downloadElapsedMs.toFixed(2)} ms, Unzip: ${unzipElapsedMs.toFixed(2)} ms, Parse: ${parseElapsedMs.toFixed(2)} ms).`,
      );

      return ZipResultSchema.parse({ analysisResponseData });
    }

    const settled = await Promise.allSettled(
      fileKeys.map(async (filename) => {
        if (signal?.aborted) {
          throw new Error('Unzipping results aborted');
        }

        let jsonText: string;
        try {
          jsonText = await zip.files[filename].async('string');
        }
        catch (err) {
          console.warn(`[SegmentationGateway] Failed extracting text from ZIP file ${filename}:`, err);
          return null;
        }

        const data = safeJsonParse(jsonText, filename);
        if (!data) {
          return null;
        }

        if (handleOrientationMetaIfPresent(data, filename)) {
          return null;
        }

        const sopUid = filename.replace(/\.json$/i, '');

        let candidate: unknown;
        const contourObjectParse = ZipContourPayloadSchema.safeParse(data);

        if (contourObjectParse.success) {
          candidate = {
            contours: contourObjectParse.data.contours,
            imageUID: contourObjectParse.data.imageUID || sopUid,
          };
        }

        const parseRes = BatchResultSchema.safeParse(candidate);
        if (parseRes.success) {
          return parseRes.data;
        }
        else {
          console.warn(`[SegmentationGateway] Invalid contour result format in ${filename}:`, parseRes.error, 'real obj:', jsonText);
          return null;
        }
      }),
    );

    const parseElapsedMs = performance.now() - parseT0;
    const totalElapsedMs = performance.now() - t0;

    if (signal?.aborted) {
      throw new Error('Unzipping results aborted');
    }

    const batchResults: BatchResult[] = [];
    for (const outcome of settled) {
      if (outcome.status === 'fulfilled' && outcome.value != null) {
        batchResults.push(outcome.value);
      }
      else if (outcome.status === 'rejected') {
        console.warn('Error parsing a ZIP image result:', outcome.reason);
      }
    }

    console.log(
      `[SegmentationGateway] Completed ZIP retrieval of ${batchResults.length} items in ${totalElapsedMs.toFixed(2)} ms total `
      + `(Download: ${downloadElapsedMs.toFixed(2)} ms, Unzip: ${unzipElapsedMs.toFixed(2)} ms, Parse: ${parseElapsedMs.toFixed(2)} ms).`,
    );

    return ZipResultSchema.parse({ batchResults });
  }

  /**
   * Process a single batch using the Gateway service flow:
   * 1. Submit POST /jobs/segmentation
   * 2. Poll GET /jobs/{id} until complete
   * 3. Fetch image output JSONs from Tigris presigned URLs or ZIP archive
   */
  async function processBatchWithGateway(
    payload: DicomPullRequest[],
    metaData: MetaData,
    settings: SegmentationSettings,
    options: ProcessBatchGatewayOptions = {},
  ): Promise<BatchResult[]> {
    const { onProgress, signal, userId, studyInstanceUid } = options;
    const activeUserId = userId || session.value?.user?.id;
    if (!activeUserId) {
      throw new Error('No user ID found.');
    }

    const studyUid = studyInstanceUid || payload[0]?.StudyInstanceUID || '';

    if (onProgress) {
      onProgress(0, 'Submitting job to Gateway...');
    }

    const created = await createSegmentationJob(
      {
        user_id: activeUserId,
        study_instance_uid: studyUid,
        payload,
        metaData,
        settings,
      },
      signal,
    );

    if (onProgress) {
      onProgress(
        created.status.progress || 5,
        `Job queued (ID: ${created.job_id}). Polling status...`,
      );
    }

    const completedJob = await pollJobUntilComplete(created.job_id, {
      signal,
      onProgress: (st) => {
        if (onProgress) {
          const prog = Math.min(Math.max(st.progress, 5), 95);
          const msg = st.message || `Status: ${st.status}... (${prog.toFixed(0)}%)`;
          onProgress(prog, msg);
        }
      },
    });

    if (onProgress) {
      onProgress(95, 'Downloading segmentation results from storage...');
    }

    let batchResults: BatchResult[] = [];
    if (completedJob.zip_url) {
      console.log('[SegmentationGateway] zip_url detected on completed job! Extracting via ZIP flow.');
      const zipRes = await fetchJobZipResults(completedJob.zip_url, signal);
      batchResults = zipRes.batchResults || [];
    }
    else {
      throw new Error('No zip_url found on completed job.');
    }

    if (onProgress) {
      onProgress(100, 'Gateway segmentation complete.');
    }

    return batchResults;
  }

  return {
    getGatewayBaseUrl,
    createSegmentationJob,
    getJob,
    listJobs,
    abortJob,
    pollJobUntilComplete,
    fetchJobZipResults,
    processBatchWithGateway,
  };
}
